import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { map, Observable, of, take, tap } from 'rxjs';
import {
  DeviceDto,
  PartDto,
  DeviceProjection,
  DeviceUpdate,
} from '../models/device.model';
import { DeviceService } from './device.service';

type DeviceState = {
  devices: DeviceDto[];
  parts: PartDto[];
  loading: boolean;
  saving: boolean;
  error: unknown | null;
};

const DEFAULT_LIST_PROJECTION: DeviceProjection = 'BRIEF';
const DEFAULT_SINGLE_DEVICE_PROJECTION: DeviceProjection = 'FULL';

const initialState: DeviceState = {
  devices: [],
  parts: [],
  loading: false,
  saving: false,
  error: null,
};

export interface GetDevicePayload {
  deviceId: string;
  projection?: DeviceProjection;
}

export interface UpdateDevicePayload {
  deviceId: string;
  payload: DeviceUpdate;
}

type RemoveDeviceMode = 'remove' | 'mark-unassigned';

export const DeviceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    hasDevices: computed(() => store.devices().length > 0),
    hasParts: computed(() => store.parts().length > 0),
    busy: computed(() => store.loading() || store.saving()),
  })),
  withMethods((store, deviceService = inject(DeviceService)) => {
    const normalizeDeviceProjection = (
      device: DeviceDto,
      projection: DeviceProjection,
    ): DeviceDto => ({
      ...device,
      projection: device.projection ?? projection,
      licenseBatches: device.licenseBatches ?? [],
    });

    const normalizeDevicesProjection = (
      devices: DeviceDto[],
      projection: DeviceProjection,
    ): DeviceDto[] =>
      devices.map((device) => normalizeDeviceProjection(device, projection));

    const upsertDevice = (
      devices: DeviceDto[],
      device: DeviceDto,
      fallbackDeviceId: string,
    ): DeviceDto[] => {
      const resolvedDeviceId = device.deviceId ?? fallbackDeviceId;
      let foundMatch = false;
      const updatedDevices = devices.map((existingDevice) => {
        if (existingDevice.deviceId !== resolvedDeviceId) {
          return existingDevice;
        }
        foundMatch = true;
        return { ...existingDevice, ...device };
      });

      if (foundMatch) {
        return updatedDevices;
      }

      return [...updatedDevices, { ...device, deviceId: resolvedDeviceId }];
    };

    const removeDeviceById = (
      devices: DeviceDto[],
      deviceId: string,
    ): DeviceDto[] => {
      const normalizedDeviceId = deviceId.trim();
      if (!normalizedDeviceId) {
        return devices;
      }
      return devices.filter(
        (device) => device.deviceId?.trim() !== normalizedDeviceId,
      );
    };

    const fetchDevice$ = ({
      deviceId,
      projection = DEFAULT_SINGLE_DEVICE_PROJECTION,
    }: GetDevicePayload): Observable<DeviceDto> => {
      patchState(store, { loading: true, error: null });
      return deviceService.getDevice(deviceId, projection).pipe(
        take(1),
        map((device) => normalizeDeviceProjection(device, projection)),
        tap({
          next: (device) => {
            patchState(store, {
              devices: upsertDevice(store.devices(), device, deviceId),
              loading: false,
            });
          },
          error: (error: unknown) => {
            patchState(store, { error, loading: false });
          },
        }),
      );
    };

    return {
      getCompanyDevices(
        projection: DeviceProjection = DEFAULT_LIST_PROJECTION,
      ): void {
        patchState(store, { loading: true, error: null });
        deviceService
          .getCompanyDevices(projection)
          .pipe(take(1))
          .subscribe({
            next: (result) => {
              patchState(store, {
                devices: normalizeDevicesProjection(
                  result.devices ?? [],
                  projection,
                ),
                loading: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, loading: false });
            },
          });
      },

      getAllDevices(
        projection: DeviceProjection = DEFAULT_LIST_PROJECTION,
      ): void {
        patchState(store, { loading: true, error: null });
        deviceService
          .getAllDevices(projection)
          .pipe(take(1))
          .subscribe({
            next: (result) => {
              patchState(store, {
                devices: normalizeDevicesProjection(
                  result.devices ?? [],
                  projection,
                ),
                loading: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, loading: false });
            },
          });
      },

      getDevice(payload: GetDevicePayload): void {
        fetchDevice$(payload).subscribe();
      },

      getDeviceFromStoreOrFetch({
        deviceId,
        projection = DEFAULT_SINGLE_DEVICE_PROJECTION,
      }: GetDevicePayload): Observable<DeviceDto> {
        const cachedDevice =
          store.devices().find((device) => {
            if (device.deviceId !== deviceId) {
              return false;
            }
            return device.projection === projection;
          }) ?? null;

        if (cachedDevice) {
          patchState(store, { error: null });
          return of(cachedDevice);
        }

        return fetchDevice$({ deviceId, projection });
      },

      getDeviceParts(deviceId: string): void {
        patchState(store, { loading: true, error: null });
        deviceService
          .getDeviceParts(deviceId)
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              patchState(store, {
                parts: response.parts ?? [],
                loading: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, loading: false });
            },
          });
      },

      updateDevice({ deviceId, payload }: UpdateDevicePayload): void {
        patchState(store, { saving: true, error: null });
        deviceService
          .updateDevice(deviceId, payload)
          .pipe(take(1))
          .subscribe({
            next: (device) => {
              patchState(store, {
                devices: upsertDevice(
                  store.devices(),
                  normalizeDeviceProjection(
                    device,
                    device.projection ?? DEFAULT_SINGLE_DEVICE_PROJECTION,
                  ),
                  deviceId,
                ),
                saving: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, saving: false });
            },
          });
      },

      removeDeviceFromCompany(deviceId: string): void {
        const normalizedDeviceId = deviceId.trim();
        if (!normalizedDeviceId) {
          return;
        }

        patchState(store, { saving: true, error: null });
        deviceService
          .removeDeviceFromCompany(normalizedDeviceId)
          .pipe(take(1))
          .subscribe({
            next: () => {
              patchState(store, {
                devices: removeDeviceById(store.devices(), normalizedDeviceId),
                saving: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, saving: false });
            },
          });
      },

      removeDeviceFromCompanyWithMode(
        deviceId: string,
        mode: RemoveDeviceMode = 'remove',
      ): void {
        const normalizedDeviceId = deviceId.trim();
        if (!normalizedDeviceId) {
          return;
        }

        patchState(store, { saving: true, error: null });
        deviceService
          .removeDeviceFromCompany(normalizedDeviceId)
          .pipe(take(1))
          .subscribe({
            next: () => {
              const updatedDevices =
                mode === 'mark-unassigned'
                  ? store.devices().map((device) => {
                      if (device.deviceId?.trim() !== normalizedDeviceId) {
                        return device;
                      }
                      return { ...device, company: undefined };
                    })
                  : removeDeviceById(store.devices(), normalizedDeviceId);

              patchState(store, {
                devices: updatedDevices,
                saving: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, saving: false });
            },
          });
      },

      deleteDevice(deviceId: string): void {
        const normalizedDeviceId = deviceId.trim();
        if (!normalizedDeviceId) {
          return;
        }

        patchState(store, { saving: true, error: null });
        deviceService
          .deleteDevice(normalizedDeviceId)
          .pipe(take(1))
          .subscribe({
            next: () => {
              patchState(store, {
                devices: removeDeviceById(store.devices(), normalizedDeviceId),
                saving: false,
              });
            },
            error: (error: unknown) => {
              patchState(store, { error, saving: false });
            },
          });
      },

      clearError(): void {
        patchState(store, { error: null });
      },

      reset(): void {
        patchState(store, initialState);
      },
    };
  }),
);
